/*
 * fn: fetchApi
 *
 * Wrapper for `fetch`, for EDHREC API requests
 *
 */
import { EDHRECError } from '@/defs/error'
import { ENDPOINT_BASES, type EndpointBaseType } from '@/defs/misc'

export type RequestPropsType = {
  base: EndpointBaseType
  options?: RequestInit
  path: string
  requiresAuth?: boolean
}

export type RequestResponseType<T> = {
  data: T
  headers: Headers
}

export const fetchApi = async <T>({
  base,
  options,
  path,
  requiresAuth,
}: RequestPropsType): Promise<RequestResponseType<T>> => {
  const origin = ENDPOINT_BASES[base]
  if (origin === undefined) {
    throw new EDHRECError({
      base,
      message: `[requestUtil/fetchApi] Endpoint base "${base}" is not configured, path: ${path}`,
      statusCode: 500,
    })
  }

  if (requiresAuth) {
    const firebaseAuthUtil = await import('@/utils/firebaseAuthUtil')
    const token = await firebaseAuthUtil.fetchFirebaseToken()
    const headers = new Headers(options?.headers)
    headers.set('Authorization', `Bearer ${token}`)
    options = { ...options, headers }
  }

  const url = `${origin}${path}`

  let response: Response
  try {
    response = await fetch(url, options)
  } catch (e) {
    let message = `[requestUtil/fetchApi] Failed to fetch ${url}`
    if (e instanceof Error) message += `: (${e.message})`
    throw new EDHRECError({ base, cause: e, message, statusCode: 500 })
  }

  if (!response.ok) {
    throw new EDHRECError({
      base,
      message: `[requestUtil/fetchApi] Endpoint response not ok: (${url}, Status: ${response.status})`,
      statusCode: response.status,
    })
  }

  let data: T
  try {
    data = (await response.json()) as T
  } catch (e) {
    let message = `[requestUtil/fetchApi] Failed to parse JSON from ${url}`
    if (e instanceof Error) message += `: (${e.message})`
    throw new EDHRECError({ base, cause: e, message, statusCode: 500 })
  }

  return { data, headers: response.headers }
}
